/*
 * (c) Copyright 2024 Calabrio, Inc.
 * All Rights Reserved. www.calabrio.com LICENSED MATERIALS
 * Property of Calabrio, Inc., Minnesota, USA
 *
 * No part of this publication may be reproduced, stored or transmitted,
 * in any form or by any means (electronic, mechanical, photocopying,
 * recording or otherwise) without prior written permission from Calabrio, Inc.
 */

import Vue from 'vue'
import Router from 'vue-router'
import store from './store/indexBA'
import defaultRoutes from './router/default.js'
import deepmerge from 'deepmerge';
import WyConstants from './library/wy-constants.js';

Vue.use(Router)
const router = new Router({
  routes: defaultRoutes
})

router.checkUnsaveChanges = (callback) => {
  let vue = router.app;
  if(vue.unsaveDataChanages) {        
    vue.$confirm(vue.$t('warning.unsavedDataMessage'), vue.$t('warning.unsavedDataHeader'), {
      showClose: false,
      confirmButtonText: vue.$t('button.cancel'),
      cancelButtonText: vue.$t('button.confirm'),
      customClass: 'wys-message-box wy-unsaved-change-alert',
      confirmButtonClass: 'wy-grey-button',
      cancelButtonClass: 'wy-blue-button',
      type: 'warning'
    }).then(() => {
      // do nothing  
    }).catch(() => { 
      vue.unsaveDataChanages = false;
      if(callback) callback();
    });
  }
  else {
    if(callback) callback();
  }
}

router.toWhereFullPath = (inWhere) => {
  if(inWhere) {
    const where = JSON.parse(JSON.stringify(inWhere));
    where.full_path = `${where.section}|${where.page}|${where.view}`;
    return where;
  }
  return null;
}

router.logAmplitudeEvent = ({eventName, view, eventInfo, filterProperties}) => {
  if (router.history.current.meta.track) {
    eventInfo = eventInfo || {};
    
    const whereInfo = router.toWhereFullPath(router.history.current.meta.track.where);
    if(view) {
      whereInfo.view = view;
    }
    
    if(!eventInfo.where) eventInfo.where = whereInfo;

    store.dispatch('logAmplitudeWithFilters', {eventName: eventName, eventProperties: eventInfo, filterProperties: filterProperties});
  }
}

router.logAmplitudeEventWithoutTrack = ({eventName, eventInfo, filterProperties}) => {
  
  eventInfo = eventInfo || {};
  
  store.dispatch('logAmplitudeWithFilters', {eventName: eventName, eventProperties: eventInfo, filterProperties: filterProperties});
}

router.logAmplitudeEventSubPage = (viewName, additionalProps) => {
  if(router.history.current.meta.track) {
    router.history.current.meta.track.previousWhere = JSON.parse(JSON.stringify(router.history.current.meta.track.where));
  }

  router.history.current.meta.track.where.view = viewName || router.history.current.meta.track.first_where.view;

  // log page landing
  if(router.history.current.meta.track) {
    let trackInfo = JSON.parse(JSON.stringify(router.history.current.meta.track));
    trackInfo.where = router.toWhereFullPath(trackInfo.where);
    trackInfo.previousWhere = router.toWhereFullPath(trackInfo.previousWhere);

    let section_switch = false;
    let page_switch = false;
    if(trackInfo.where && trackInfo.previousWhere) {
      if(trackInfo.where.section != trackInfo.previousWhere.section) {
        section_switch = true
      }
      if(trackInfo.where.section != trackInfo.previousWhere.section || trackInfo.where.page != trackInfo.previousWhere.page) {
        page_switch = true
      }
    }
    trackInfo.section_switch = section_switch
    trackInfo.page_switch = page_switch;

    delete trackInfo.first_where;
    delete trackInfo.whereOrigin;

    if(additionalProps) {
      trackInfo = Object.assign(trackInfo, additionalProps);
    }

    store.dispatch('logAmplitudeWithFilters', {eventName: 'view_opening_initiated', eventProperties: trackInfo});
  }
}

router.logAmplitudeEventPageTimeSpent = (fromTtrack, toTrack) => {
  // prepare amplitube tracking information
  if(toTrack) {    
    // log time spent on page
    if(fromTtrack && fromTtrack.first_where) {
      if(store.state.auth.trackInfo && store.state.auth.trackInfo.loadedTimeLong > 0) {
        // log page leaving
        const trackInfo = JSON.parse(JSON.stringify(fromTtrack));
        trackInfo.first_where = router.toWhereFullPath(fromTtrack.first_where);
        trackInfo.last_where = router.toWhereFullPath(fromTtrack.where);
        trackInfo.next_where = router.toWhereFullPath(toTrack.where);
        delete trackInfo.where;
        delete trackInfo.previousWhere;
        delete trackInfo.whereOrigin;
        store.dispatch('logAmplitudeWithFilters', {eventName: 'page_time_spent', eventProperties: trackInfo});        
      }
    }
  }
}

router.afterEach((to, from) => {
  router.logAmplitudeEventPageTimeSpent(from.meta.track, to.meta.track);

  if(from.meta.track && to.meta.track) {
    to.meta.track.previousWhere = from.meta.track.where;
  }

  // log page landing
  if(to.meta.track && to.meta.track.where.section != "System" && to.path != "/reload") {
    const trackInfo = JSON.parse(JSON.stringify(to.meta.track));
    trackInfo.where = router.toWhereFullPath(trackInfo.where);
    trackInfo.previousWhere = router.toWhereFullPath(trackInfo.previousWhere);
    
    let section_switch = false;
    let page_switch = false;
    if(trackInfo.where && trackInfo.previousWhere) {
      if(trackInfo.where.section != trackInfo.previousWhere.section) {
        section_switch = true
      }
      if(trackInfo.where.section != trackInfo.previousWhere.section || trackInfo.where.page != trackInfo.previousWhere.page) {
        page_switch = true
      }
    }
    let companySwitched = localStorage.getItem("companySwitched")
    if(companySwitched == 'true') {
      trackInfo.previousWhere = {
        "full_path": "System | User Switched Company | User Switched Company",
        "section" : "System",
        "page" : "User Switched Company",
        "view" : "User Switched Company"
      }
      localStorage.setItem("companySwitched", 'false');
      section_switch = true;
      page_switch = true;
    }
    trackInfo.section_switch = section_switch
    trackInfo.page_switch = page_switch;

    delete trackInfo.first_where;
    delete trackInfo.whereOrigin;
    store.dispatch('logAmplitudeWithFilters', {eventName: 'view_opening_initiated', eventProperties: trackInfo});
  }
})

router.beforeEach(async (to, from, next) => {
  if(to.path == "/reload" && from.meta.track) {
    from.meta.track.previousWhere = JSON.parse(JSON.stringify(from.meta.track.where));
  }
  
  if(to.params && to.params.whereView) {
    if(to.meta.track && to.meta.track.where) to.meta.track.where.view = to.params.whereView;
    if(to.meta.track && to.meta.track.first_where) to.meta.track.first_where.view = to.params.whereView;
  }
  else if(to.meta.track && to.meta.track.whereOrigin) {
    if(to.meta.track && to.meta.track.where) to.meta.track.where.view = to.meta.track.whereOrigin.view;
    if(to.meta.track && to.meta.track.first_where) to.meta.track.first_where.view = to.meta.track.whereOrigin.view;
  }
  
  // remove events listener
  if(router.app.unregisterPageEvent) router.app.unregisterPageEvent(from.name);
  if(router.app.hideNotification) router.app.hideNotification();

  // set current page object name
  let bindModel = to.meta.bindModel;
  if(bindModel) {
    await store.dispatch('setCurrentPage', {pageName: to.name, bindModel: bindModel});
  }
  
  if(to.query.props && to.query.props != "") {
    sessionStorage.setItem(WyConstants.CONV_INTELLIGENCE_PROPS_STORAGE_KEY, to.query.props);
  }
  
  if(to.query.sid && to.query.sid != "" && 
    (!store.getters.authUser 
      || store.getters.authUser.sessionId != to.query.sid 
    )) {
    const authHost = to.query.host || window.parent.location.href;
    const sessionId = to.query.sid;
    const authUrl = window.location.href;
    const authOptions = to.query.options;

    await store.dispatch('c1Auth', {authHost: authHost, authOptions: authOptions, sessionId: sessionId, authUrl: authUrl, productName: WyConstants.PRODUCT_NAME_BA});

    next("/default");
  }
  else if(to.path == "/") {
    //store.commit('updateConfig', {});

    if(store.getters.isLoggedIn) {
      // goto default page
      // let defaultPath = (store.getters.config && store.getters.config.navMenu ? store.getters.config.navMenu.defaultPath : "/default");
      // next(defaultPath);
      next("/default");
    }
    else {      
      next();
    }
  }
  else if(to.meta.pub) {
    next();
  }
  else if(to.path == '/default') {
    next();
  }
  else {
    // check user session from the server
    if(!store.getters.authUser || !store.getters.authUser.token) {
      // if session expired, reset user data and send user to login
      store.dispatch('logout', 'sessionExpired');
      if(router.app.showNotificationLocale) {
        router.app.unsaveDataChanages = false;
        router.app.showNotificationLocale("error", "login.sessionExpiredTitle", "login.sessionExpiredMsg");
      }
      next('/');
    }
    else {
      // init pando
      if(!WyConstants.C1_DEV_MODE) {
        await store.dispatch('pendoInit');
      }
      
      // load glogal filters
      if(to.meta.filterAction) {
        await Promise.resolve({ 
          then: (resolve) => {
            setTimeout(async() => { 
              if(router.app.setLoading) router.app.setLoading(true);
              await store.dispatch(to.meta.filterAction, {pageName: to.meta.bindModel});          
              await store.dispatch('initFilterDataSource', {filterNames: to.meta.filterNames, fromPage: from.meta.bindModel, toPage: to.meta.bindModel});
              if(router.app.setLoading) router.app.setLoading(false);

              resolve(true);
            }, 800);
          }
        });

        // find original page name before forward to nodata page
        for(let menuItem of store.getters.config.navMenu.items) {
          if(menuItem.path==to.path) {
            store.commit("setCurrentPageInfo", {title: menuItem.label, tooltip: menuItem.tooltip});
            break;
          }
          else if(menuItem.subItems) {
            let subItem = menuItem.subItems.find(subItem => {
              return subItem.path==to.path;
            });

            if(subItem != null) {
              store.commit("setCurrentPageInfo", {title: subItem.label, subtitle: menuItem.label, tooltip: subItem.tooltip});
              break;
            }
          }
        }

        if(!await store.dispatch('checkHasFilter', {toPage: to.meta.bindModel, toPath: to.path})) {
          store.dispatch('updateDefaultRoutePath', to.path);
          // goto nodata page
          
          router.push({ path: '/nodata' });
          return;
        }
        else if(store.getters.shareState.warning && store.getters.shareState.warningMessages.length > 0) {
          router.app.showNotification("warning", null, store.getters.shareState.warningMessages, {duration: 1000000000, showClose: true, width: 500});
        }
      }

      let defaultPath = null;
      if(store.getters.config && !store.getters.config.isInited) {
        store.getters.config.isInited = true;

        await store.dispatch('fetchAllConfigurations');
        await store.dispatch('fetchCompanyProfileSettings');

        // load default config        
        let companyConfig = store.getters.config;
        let authUser = store.getters.authUser;

        if(authUser.company && authUser.company.systemProperty) {
          let productCode = authUser.company.selectedProduct.productCode;
          let systemProperty = authUser.company.systemProperty[productCode];
          if(systemProperty && Object.keys(systemProperty).length > 0) {
            companyConfig = deepmerge(companyConfig, systemProperty);
          }
        }

        // init amplitude
        let amplitudeMap = store.getters.globalConfig.amplitudeMap;
        if(amplitudeMap) {
            let locationHost = location.host.replace(/\./g, '_');
            let amplitudeKey = amplitudeMap[locationHost] || amplitudeMap["default"];
            if(amplitudeKey) {
                // eslint-disable-next-line no-undef
                amplitude.getInstance().init(amplitudeKey);
            }
        }

        // map menu item with path
        const pathToMenuItem = {};
        store.getters.config.navMenu.items.map(menu => {
          if(menu.path) {
            pathToMenuItem[menu.path] = {
              menuItem: menu
            };
          }
          if(menu.subItems && menu.subItems.length > 0) {
            menu.subItems.map(item => {
              if(item.path) {
                pathToMenuItem[item.path] = {
                  parentItem: menu,
                  menuItem: item
                }
              }
            })
          }
        });

        // get curren company products configs
        const currCompanyProducts = companyConfig.appProducts.filter(prod => authUser.company.products.find(el => (el.productCode >= prod.code && el.productCode <= prod.code+99)));
        currCompanyProducts.map(prod => {
          const appRouter = require(`./router/${prod.key}.js`).default;
          appRouter.map(routerItem => {
            routerItem.meta.track = {where: {}};
            if(pathToMenuItem[routerItem.path]) {
              const parentItem = pathToMenuItem[routerItem.path].parentItem;
              const menuItem = pathToMenuItem[routerItem.path].menuItem;
              if(parentItem) {
                routerItem.meta.track.where.section = parentItem.label;                
              }
              else {
                routerItem.meta.track.where.section = menuItem.label;
              }
              routerItem.meta.track.where.page = menuItem.sectionName || menuItem.label;
              routerItem.meta.track.where.view = menuItem.viewName || menuItem.label;

              routerItem.meta.track.first_where = JSON.parse(JSON.stringify(routerItem.meta.track.where));
              routerItem.meta.track.whereOrigin = JSON.parse(JSON.stringify(routerItem.meta.track.where));
            }
          })
          
          router.addRoutes(appRouter);
        });

        // DEMO: for demo purpose set demo produce code
        const defaultNavMenuConfigs = (store.getters.companyConfig.default || {}).navMenu;
        const demoConfigs = store.getters.companyConfig.demoConfigs || {};
        const currNavMenu = companyConfig.navMenu.items.filter(item => currCompanyProducts.find(el => (el.code==item.productCode || item.permission || demoConfigs.isDemo)));
        companyConfig.navMenu.items = currNavMenu;

        if(defaultNavMenuConfigs) {
          currNavMenu.map(menu => {
            // set default behaviours expand/collapse          
            menu.isExtend = defaultNavMenuConfigs.isExpand || false;
          });
        }

        // check if this businessInsights menu has configured pdf files
        const businessInsightsMenu = companyConfig.navMenu.items.find(menu => menu.productCode==4500);
        if(businessInsightsMenu && businessInsightsMenu.subItems) {
          const executiveReportsMenuItem = businessInsightsMenu.subItems.find(subItem => subItem.path=="/businessInsights/executiveReports");
          if(executiveReportsMenuItem && !store.getters.isSysAdminUser && (!store.getters.companyConfig.businessInsightBmrFiles || store.getters.companyConfig.businessInsightBmrFiles.length == 0)) {
            executiveReportsMenuItem.isHidden = true;
          }
          const performanceReportsMenuItem = businessInsightsMenu.subItems.find(subItem => subItem.path=="/businessInsights/performanceReports");
          if(performanceReportsMenuItem && !store.getters.isSysAdminUser && (!store.getters.companyConfig.businessInsightBprFiles || store.getters.companyConfig.businessInsightBprFiles.length == 0)) {
            performanceReportsMenuItem.isHidden = true;
          }
        }

        // check if all subItems visible
        currNavMenu.map(menu => {
          if(menu.subItems) {
            const menuSubItems = menu.subItems.filter(subItem => !subItem.isHidden);
            if(menuSubItems.length == 0) {
              menu.isHidden = true;
            }
          }
        });

        // get current company products menu items
        if(defaultPath == null && store.getters.defaultRoutePath && store.getters.defaultRoutePath != "") {
          for(let menu of currNavMenu.filter(el => !el.isHidden)) {
            if(store.getters.isProductAllow(menu)) {
              if(menu.subItems && menu.subItems.length > 0) {
                menu.isExtend = true;

                for(let subItem of menu.subItems) {
                  if(!subItem.isHidden && store.getters.isRoleAllow(subItem.aclCodes) && subItem.path==store.getters.defaultRoutePath) {
                    defaultPath = store.getters.defaultRoutePath;
                    break;
                  }
                }
              }
              else if(store.getters.isRoleAllow(menu.aclCodes) && menu.path == store.getters.defaultRoutePath) {
                menu.isExtend = true;
                defaultPath = store.getters.defaultRoutePath;
              }
            }

            if(defaultPath != null) break;
          }
        }

        if(defaultPath == null && currNavMenu && currNavMenu.length > 0) {
          for(let menu of currNavMenu.filter(el => !el.isHidden)) {
            if(store.getters.isProductAllow(menu)) {
              if(menu.subItems && menu.subItems.length > 0) {
                menu.isExtend = true;

                for(let subItem of menu.subItems) {
                  if(!subItem.isHidden && store.getters.isRoleAllow(subItem.aclCodes)) {
                    defaultPath = subItem.path;
                    break;
                  }
                }
              }
              else if(store.getters.isRoleAllow(menu.aclCodes)) {
                menu.isExtend = true;
                defaultPath = menu.path
              }
              break;
            }

            if(defaultPath != null) break;
          }
        }

        // set default router page
        store.commit("updateConfig", companyConfig);

        // update nav menu
        // if(!store.state.site.currentNavMenu) {
          store.commit('updateSite', {navMenu: companyConfig.navMenu});
        // }
      }

      if(defaultPath) {
        store.dispatch('updateDefaultRoutePath', defaultPath);
        // goto default page
        router.push({ path: defaultPath })
      }
      else {
        store.dispatch('updateDefaultRoutePath', to.path);
        // alert if there are unsave data
        router.checkUnsaveChanges(next);
      }
    }
  }
})

// alert unsave changes warning when user leave this page
/*window.addEventListener('beforeunload', function (event) {
  let vue = router.app;
  if(vue.unsaveDataChanages) {    
    event.returnValue = vue.$t('warning.unsavedDataMessage');
  }
}, false)*/

export default router
